<template>
  <PbsActionButton
    v-if="props.detail"
    :label="$t('general.moreActions')"
    :items="actionButtonItems"
    :mod-vertical="modVertical"
    :mod-large="modLarge"
  />
</template>
<script lang="ts" setup>
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import PbsActionButton, { PbsActionButtonItem } from '@/components/PbsActionButton.vue';
import { useModalStore } from '@/modules/core/store/modal.store';
import DefinitionStatusForm from '../definition-status-form/DefinitionStatusForm.vue';
import DefinitionDuplicationForm from '../definition-duplication-form/DefinitionDuplicationForm.vue';
import DefinitionExportForm from '../definition-export-form/DefinitionExportForm.vue';
import DefinitionDryRunForm from '../definition-dryrun-form/DefinitionDryRunForm.vue';
import DefinitionCascadeDeleteForm from '../definition-cascade-delete-form/DefinitionCascadeDeleteForm.vue';
import { ProductProcessDefinitionDetailDto, ProductProcessDefinitionSearchResultDto } from '@/api/backoffice/clients';
import { ProductProcessDefinitionActionButtonType } from '@/modules/configuration/common/product-process-definition.common';
import { useProductProcessDefinitionDetailStore } from '@/modules/configuration/store/product-process-definition/product-process-definition-detail.store';

const store = useProductProcessDefinitionDetailStore();
const modalStore = useModalStore();
const router = useRouter();

const { t } = useI18n();

type ProductProcessDefinitionActionButtonProps = {
  detail: ProductProcessDefinitionDetailDto | ProductProcessDefinitionSearchResultDto;
  modVertical?: boolean;
  modLarge?: boolean;
  actions: ProductProcessDefinitionActionButtonType[];
};

const props = withDefaults(defineProps<ProductProcessDefinitionActionButtonProps>(), {
  modVertical: false,
  modLarge: false,
  detail: null,
  actions: () => []
});

const actionButtonItems = computed((): PbsActionButtonItem[] => {
  const items = createActionButtonItems();
  return props.actions?.map((action) => items.find((x) => x.name === action)).filter((x) => !!x) ?? [];
});

const createActionButtonItems = () => {
  const isDraft = props.detail?.statusId === 0;

  return [
    {
      name: ProductProcessDefinitionActionButtonType.ChangeStatus,
      label: t('productprocessdefinition.actions.changeStatus.label'),
      click: showChangeStatusModal,
      ellipsis: true
    },
    {
      name: ProductProcessDefinitionActionButtonType.Duplicate,
      label: t('productprocessdefinition.actions.duplicate.label'),
      click: showDuplicationModal,
      ellipsis: true
    },
    {
      name: ProductProcessDefinitionActionButtonType.DryRun,
      label: t('productprocessdefinition.actions.dryrun.label'),
      click: showDryRunModal,
      disabled: !isDraft,
      ellipsis: true
    },
    {
      name: ProductProcessDefinitionActionButtonType.CascadeDelete,
      label: t('productprocessdefinition.actions.cascadeDelete.label'),
      click: showCascadeDeleteModal,
      disabled: !isDraft,
      ellipsis: true
    },
    {
      name: ProductProcessDefinitionActionButtonType.Export,
      label: t('general.export'),
      click: showExportModal,
      ellipsis: true
    },
    {
      name: ProductProcessDefinitionActionButtonType.Dependencies,
      label: t('productprocessdefinition.actions.dependencies.label'),
      click: navigateToDependenciesRoute
    },
    {
      name: ProductProcessDefinitionActionButtonType.GenerateReport,
      label: t('deliveryprocessdefinition.actions.generateReport.label'),
      click: generateReport
    }
  ];
};

const navigateToDependenciesRoute = () => {
  const id = props.detail.id;

  router.push({ name: 'productprocessdefinition.detail.dependencies', params: { id } });
};

const showChangeStatusModal = () => {
  const modalContent = {
    title: t('productprocessdefinition.actions.changeStatus.description'),
    component: DefinitionStatusForm,
    props: {
      id: props.detail.id
    }
  };

  modalStore.showModal(modalContent);
};

const showDuplicationModal = () => {
  const modalContent = {
    title: t('productprocessdefinition.actions.duplicate.description'),
    component: DefinitionDuplicationForm,
    props: {
      id: props.detail.id
    }
  };

  modalStore.showModal(modalContent);
};

const showDryRunModal = () => {
  const modalContent = {
    title: t('productprocessdefinition.actions.dryrun.description'),
    component: DefinitionDryRunForm,
    props: {
      id: props.detail.id
    }
  };

  modalStore.showModal(modalContent);
};

const showCascadeDeleteModal = () => {
  const modalContent = {
    closable: false,
    title: t('productprocessdefinition.actions.cascadeDelete.description'),
    component: DefinitionCascadeDeleteForm,
    props: {
      id: props.detail.id
    }
  };

  modalStore.showModal(modalContent);
};

const showExportModal = () => {
  const modalContent = {
    title: t('productprocessdefinition.actions.export.description'),
    component: DefinitionExportForm,
    props: {
      id: props.detail.id
    }
  };

  modalStore.showModal(modalContent);
};

const generateReport = async () => {
  const id = props.detail.id;
  await store.generateReport(id, {});
};
</script>

<style lang="scss" scoped></style>
